/*
 * Tennis bracelet configurator (sections/tennis-bracelet-product.liquid).
 * Vanilla, no Alpine: resolves the selected carat + gold color to a variant
 * from the embedded JSON map, updates the hidden form id / prices /
 * availability, and keeps the draggable scroll-snap gallery, arrows and
 * thumbnails in sync. Add to Cart itself is handled by the global xProductCart component
 * (product-cart.js).
 */
if (!window.Eurus || !window.Eurus.loadedScript.has('tennis-bracelet-product.js')) {
  if (window.Eurus) window.Eurus.loadedScript.add('tennis-bracelet-product.js');

  (() => {
    const init = (root) => {
      if (!root || root.dataset.tbReady) return;
      root.dataset.tbReady = 'true';

      let variants = [];
      try {
        variants = JSON.parse(root.querySelector('[data-tb-variants]').textContent);
      } catch (error) {
        console.error('tennis-bracelet-product: invalid variant map', error);
        return;
      }

      const idInput = root.querySelector('[data-tb-id]');
      const atc = root.querySelector('[data-tb-atc]');
      const atcLabel = root.querySelector('[data-tb-atc-label]');
      const priceEl = root.querySelector('[data-tb-price]');
      const descriptionEl = root.querySelector('[data-tb-variant-description]');
      const caratInputs = Array.from(root.querySelectorAll('[data-tb-carat]'));
      const metalInputs = Array.from(root.querySelectorAll('[data-tb-metal]'));
      const strip = root.querySelector('[data-tb-gallery]');
      const slides = strip ? Array.from(strip.querySelectorAll('[data-tb-slide]')) : [];
      const thumbs = Array.from(root.querySelectorAll('[data-tb-thumb]'));
      const previousButton = root.querySelector('[data-tb-gallery-prev]');
      const nextButton = root.querySelector('[data-tb-gallery-next]');
      let activeIndex = 0;
      let scrollFrame = null;

      const checkedValue = (inputs) => {
        const input = inputs.find((el) => el.checked);
        return input ? input.value : null;
      };

      const findVariant = (carat, metal) => variants.find((v) => {
        const caratOk = v.c === null || carat === null || v.c === carat;
        const metalOk = v.m === null || metal === null || v.m === metal;
        return caratOk && metalOk;
      });

      const scrollInsideRow = (element, inline) => {
        if (!element) return;
        const pageX = window.scrollX;
        const pageY = window.scrollY;

        element.scrollIntoView({
          inline,
          block: 'nearest',
          behavior: 'auto'
        });

        if (window.scrollX !== pageX || window.scrollY !== pageY) {
          window.scrollTo(pageX, pageY);
        }
      };

      // Browser-native alignment handles scroll snapping consistently in LTR
      // and RTL; scrollInsideRow prevents it from moving the document itself.
      const goTo = (index) => {
        const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
        scrollInsideRow(slides[safeIndex], 'start');
      };

      const markThumb = (index) => {
        activeIndex = index;
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === index);
          if (i === index) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        if (previousButton) previousButton.disabled = index === 0;
        if (nextButton) nextButton.disabled = index === slides.length - 1;
      };

      const keepThumbVisible = (thumb) => scrollInsideRow(thumb, 'nearest');

      const update = (scrollToVariantImage) => {
        const carat = checkedValue(caratInputs);
        const metal = checkedValue(metalInputs);
        const variant = findVariant(carat, metal);

        // Re-price the carat cards for the selected gold color.
        caratInputs.forEach((input) => {
          const card = input.closest('.tb-card');
          if (!card) return;
          const cardVariant = findVariant(input.value, metal);
          const cardPrice = card.querySelector('[data-tb-card-price]');
          if (cardPrice) cardPrice.textContent = cardVariant && cardVariant.p ? cardVariant.p : '—';
          card.classList.toggle('is-off', !cardVariant || !cardVariant.a);
        });

        if (variant && idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = (variant && variant.p) || '';
        if (descriptionEl) {
          const description = (variant && variant.d) || '';
          descriptionEl.innerHTML = description;
          descriptionEl.hidden = !description;
        }

        if (atc) {
          atc.disabled = !variant || !variant.a;
          if (atcLabel) {
            if (!variant) {
              atcLabel.textContent = atc.dataset.labelUnavailable;
            } else {
              atcLabel.textContent = variant.a ? atc.dataset.labelAdd : atc.dataset.labelSoldout;
            }
          }
        }

        if (scrollToVariantImage && variant && variant.img) {
          const index = slides.findIndex((slide) => slide.dataset.mediaId === String(variant.img));
          if (index > -1) goTo(index);
        }
      };

      caratInputs.forEach((input) => input.addEventListener('change', () => update(true)));
      metalInputs.forEach((input) => input.addEventListener('change', () => update(true)));

      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => goTo(index)));

      if (strip && slides.length > 1) {
        const isRtl = getComputedStyle(strip).direction === 'rtl';
        const nearestSlideIndex = () => {
          const stripCenter = strip.getBoundingClientRect().left + (strip.clientWidth / 2);
          let nearestIndex = 0;
          let nearestDistance = Infinity;

          slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect();
            const distance = Math.abs((rect.left + (rect.width / 2)) - stripCenter);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
            }
          });

          return nearestIndex;
        };

        const syncGallery = () => {
          scrollFrame = null;
          const index = nearestSlideIndex();
          if (index === activeIndex) return;
          markThumb(index);
          keepThumbVisible(thumbs[index]);
        };

        if (previousButton) previousButton.addEventListener('click', () => goTo(activeIndex - 1));
        if (nextButton) nextButton.addEventListener('click', () => goTo(activeIndex + 1));

        strip.addEventListener('scroll', () => {
          if (!scrollFrame) scrollFrame = requestAnimationFrame(syncGallery);
        }, { passive: true });

        strip.addEventListener('keydown', (event) => {
          let nextIndex = null;

          if (event.key === 'ArrowLeft') nextIndex = activeIndex + (isRtl ? 1 : -1);
          if (event.key === 'ArrowRight') nextIndex = activeIndex + (isRtl ? -1 : 1);
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = slides.length - 1;
          if (nextIndex === null) return;

          event.preventDefault();
          goTo(nextIndex);
        });

        let pointerId = null;
        let pointerStart = 0;
        let scrollStart = 0;
        let didDrag = false;

        strip.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button !== 0) return;
          pointerId = event.pointerId;
          pointerStart = event.clientX;
          scrollStart = strip.scrollLeft;
          didDrag = false;
          strip.setPointerCapture(pointerId);
          strip.classList.add('is-dragging');
        });

        strip.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointerId) return;
          const delta = event.clientX - pointerStart;
          if (Math.abs(delta) > 4) didDrag = true;
          const direction = isRtl ? -1 : 1;
          strip.scrollLeft = scrollStart - (delta * direction);
        });

        const finishDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          if (strip.hasPointerCapture(pointerId)) strip.releasePointerCapture(pointerId);
          pointerId = null;
          strip.classList.remove('is-dragging');
          if (didDrag) goTo(nearestSlideIndex());
        };

        strip.addEventListener('pointerup', finishDrag);
        strip.addEventListener('pointercancel', finishDrag);
        strip.addEventListener('dragstart', (event) => event.preventDefault());
      }

      markThumb(0);
      update(false);
    };

    const boot = () => document.querySelectorAll('[data-tb-root]').forEach(init);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    document.addEventListener('shopify:section:load', (event) => {
      const root = event.target && event.target.querySelector('[data-tb-root]');
      if (root) init(root);
    });
  })();
}
